"use client";

import "@/styles/fonts-nunito.css";
import { cn } from "@/components/libs/cn";
import { Logo } from "@/components/ui/logo";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { type ReactNode, useMemo, useEffect } from "react";

const pageContainerVariants = cva(
  "flex h-full max-h-full min-h-full flex-1 flex-col items-center",
  {
    variants: {
      padding: { default: "px-4 py-6 xs:p-8", sm: "p-6" },
    },
    defaultVariants: { padding: "default" },
  },
);

type PageContainerProps = VariantProps<typeof pageContainerVariants> & {
  className?: string;
  children: ReactNode;
  maxWidth?: string;
  initialAnimation?: boolean;
  noLogo?: boolean;
  logoVariant?: "default" | "small" | "tiny";
  heightScreen?: boolean;
};

export function PageContainer({
  className,
  children,
  maxWidth = "max-w-md",
  initialAnimation = true,
  noLogo = false,
  logoVariant = "default",
  heightScreen,
  ...props
}: PageContainerProps) {
  const containerAnimationProps = useMemo(() => {
    return initialAnimation
      ? {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5 },
        }
      : {};
  }, [initialAnimation]);

  const contentAnimationProps = useMemo(() => {
    return initialAnimation
      ? {
          initial: { opacity: 0, y: 30, scale: 0.95 },
          animate: { opacity: 1, y: 0, scale: 1 },
          transition: { duration: 0.5, delay: 0.2 },
        }
      : {};
  }, [initialAnimation]);

  useEffect(() => {
    if (heightScreen) {
      document.documentElement.classList.add("h-full");
      document.body.classList.add("h-full");
    } else {
      document.documentElement.classList.remove("h-full");
      document.body.classList.remove("h-full");
    }

    return () => {
      document.documentElement.classList.remove("h-full");
      document.body.classList.remove("h-full");
    };
  }, [heightScreen]);

  return (
    <motion.div
      className={cn(
        pageContainerVariants({ className, ...props }),
        "font-nunito",
      )}
      {...props}
      {...containerAnimationProps}
    >
      {!noLogo && <Logo variant={logoVariant} />}
      <motion.div
        className={cn(
          "flex min-h-0 w-full flex-1 flex-col",
          noLogo && "h-full max-h-full min-h-full",
          maxWidth,
        )}
        {...contentAnimationProps}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
